import React, { useCallback, useContext, useState, useEffect, useRef, useMemo } from "react";
import PropTypes from "prop-types";
import { MensajesContext } from "../../../context/MensajesContext";
import { useStudent } from "../../../context/StudentContext";
import { useNavigate, useLocation } from "react-router-dom";
import "./Modal.css";
import { API_URL } from "../../../config";
import "./MensajeriaInterna.css";
import { emitUnreadChanged } from "../../../utils/unreadBus";
import { contactRoleLabelForDisplay } from "../../../utils/contactRoleLabel";
import { getHomeRouteByRole } from "../../../utils/rutas";
import { getMessagingRoleLabel } from "../../../utils/roles";
import { fetchSessionStatus } from "../../../utils/sessionAuth";
import { getCurrentUserProfile } from "../../../utils/profileCache";
import { formatMessagingContactLabel } from "../../../utils/messagingContactLabel";
import { filterMessagingContactsByRole } from "../../../utils/messagingContactFilters";
import {
  MESSAGING_DIRECTOR_SCOPE_ADMINS,
  MESSAGING_DIRECTOR_SCOPE_COURSES,
  MESSAGING_DIRECTOR_SCOPE_USERS,
  instructorHasAssignedCourse,
  isInstructorMessagingRole,
  isMessagingCourseScope,
  mapPerfilCursosToBasicList,
  MESSAGING_STAFF_SCOPE_ADMIN,
  normalizeMongoId,
  pickInstructorDefaultCursoId,
  resolveDirectorContactRoleFilter,
  resolveInstructorContactRoleFilter,
} from "../../../utils/messagingInstructorCourses";
import {
  buildConversationIdentity,
  conversationCacheKey,
  conversationRowKey,
  decorateContactForThread,
  formatConversationIdentityLabel,
  identityFromContact,
  isSameConversation,
  resolveOutgoingThreadIdentity,
} from "../../../utils/messagingConversationIdentity";
import es from "../../../data/locales/es.json";
import ConstrainedSelect from "../../../components/ConstrainedSelect";
import ViewportSelect from "../../../components/ViewportSelect";

const isMensajeriaInternaDevLog =
  typeof process !== "undefined" && process.env.NODE_ENV === "development";

function contactoId(contacto) {
  if (contacto == null) return "";
  const raw =
    typeof contacto === "object" && contacto !== null
      ? contacto.contactoId ?? contacto._id ?? contacto.id ?? contacto
      : contacto;
  return normalizeMongoId(raw);
}

function perfilCursosToIdList(cursosInscritos) {
  return (cursosInscritos ?? [])
    .map((c) => normalizeMongoId(c))
    .filter(Boolean);
}

const MESSAGING_PRESENCE_REFRESH_MS = 5 * 60 * 1000;

/**
 * Default course for alumno: only uses active campus course if it is in enrollments; if
 * both user and course expose tenantId, they must match, otherwise the first enrolled id is used.
 */
function pickAlumnoDefaultCursoId(cursosInscritoIds, activeCourse, userTenantId) {
  const ins = (cursosInscritoIds ?? []).map((id) => String(id)).filter(Boolean);
  if (ins.length === 0) {
    return null;
  }
  if (!activeCourse) {
    return ins[0];
  }
  const activeId = normalizeMongoId(activeCourse._id);
  if (!ins.includes(activeId)) {
    return ins[0];
  }
  const cTenant = activeCourse.tenantId;
  if (
    userTenantId != null &&
    String(userTenantId).trim() !== "" &&
    cTenant != null &&
    String(userTenantId).trim() !== String(cTenant)
  ) {
    return ins[0];
  }
  return activeId;
}

function normalizeScopeList(scopes) {
  return (scopes ?? [])
    .map((scope) => (typeof scope === "string" ? scope.trim() : ""))
    .filter((scope, index, list) => scope !== "" && list.indexOf(scope) === index);
}

function buildDirectorDeepLinkCandidateScopes({ cursoQP, directorScopeQP, assignedCourseScopes }) {
  if (cursoQP) {
    return normalizeScopeList([cursoQP]);
  }
  const normalizedDirectorScope = normalizeDirectorScopeParam(directorScopeQP);
  if (!normalizedDirectorScope) {
    return [MESSAGING_DIRECTOR_SCOPE_USERS];
  }

  const defaultCourseId = normalizeScopeList(assignedCourseScopes)[0] || "";
  return normalizeScopeList([resolveDirectorContactScope(normalizedDirectorScope, defaultCourseId)]);
}

function buildDeepLinkCandidateScopes({
  role,
  cursoQP,
  cursoSeleccionado,
  directorScopeQP,
  assignedCourseScopes,
  enrolledCourseScopes,
}) {
  const normalizedRole = typeof role === "string" ? role.trim() : "";
  const normalizedAssignedScopes = normalizeScopeList(assignedCourseScopes);
  const normalizedEnrolledScopes = normalizeScopeList(enrolledCourseScopes);

  if (normalizedRole === "director") {
    return buildDirectorDeepLinkCandidateScopes({
      cursoQP,
      directorScopeQP,
      assignedCourseScopes: normalizedAssignedScopes,
    });
  }

  if (isInstructorMessagingRole(normalizedRole)) {
    const allowedInstructorScopes = new Set([
      ...normalizedAssignedScopes,
      MESSAGING_STAFF_SCOPE_ADMIN,
    ]);
    return normalizeScopeList([
      allowedInstructorScopes.has(cursoQP) ? cursoQP : "",
      ...normalizedAssignedScopes,
      allowedInstructorScopes.has(cursoSeleccionado) ? cursoSeleccionado : "",
      MESSAGING_STAFF_SCOPE_ADMIN,
    ]);
  }

  if (normalizedRole === "alumno") {
    const allowedAlumnoScopes = new Set(normalizedEnrolledScopes);
    return normalizeScopeList([
      allowedAlumnoScopes.has(cursoQP) ? cursoQP : "",
      ...normalizedEnrolledScopes,
      allowedAlumnoScopes.has(cursoSeleccionado) ? cursoSeleccionado : "",
    ]);
  }

  return normalizeScopeList([
    isMessagingCourseScope(cursoQP) ? cursoQP : "",
    isMessagingCourseScope(cursoSeleccionado) ? cursoSeleccionado : "",
  ]);
}

function normalizeDirectorScopeParam(value) {
  return [
    MESSAGING_DIRECTOR_SCOPE_USERS,
    MESSAGING_DIRECTOR_SCOPE_ADMINS,
    MESSAGING_DIRECTOR_SCOPE_COURSES,
  ].includes(value)
    ? value
    : "";
}

function resolveDirectorContactScope(directorScope, defaultCourseId = null) {
  if (directorScope === MESSAGING_DIRECTOR_SCOPE_ADMINS) {
    return MESSAGING_STAFF_SCOPE_ADMIN;
  }
  if (directorScope === MESSAGING_DIRECTOR_SCOPE_COURSES) {
    return defaultCourseId || "";
  }
  return MESSAGING_DIRECTOR_SCOPE_USERS;
}

function normalizeVisibleSenderText(value) {
  return typeof value === "string" ? value.replace(/\s+/g, " ").trim() : "";
}

function isAlumnoMessagingContact(contacto) {
  return String(contactRoleLabelForDisplay(contacto) || "").toLowerCase() === "alumno";
}

function resolveUnreadPlaceholderName(row) {
  if (typeof row?.nombre === "string" && row.nombre.trim() !== "") {
    return row.nombre.trim();
  }
  if (typeof row?.email === "string" && row.email.trim() !== "") {
    return row.email.trim();
  }
  return "Conversación pendiente";
}

function resolveUnreadPlaceholderRole(row, fallbackRole) {
  if (typeof row?.rolGlobal === "string" && row.rolGlobal.trim() !== "") {
    return row.rolGlobal.trim();
  }
  if (typeof row?.rol === "string" && row.rol.trim() !== "") {
    return row.rol.trim();
  }
  return fallbackRole;
}

function buildUnreadContactPlaceholder(row, fallbackRole = "") {
  const id = contactoId(row?.contactoId);
  if (!id) return null;
  const count = Number(row?.count) || 0;
  const name = resolveUnreadPlaceholderName(row);
  const role = resolveUnreadPlaceholderRole(row, fallbackRole);
  const identity = buildConversationIdentity({
    key: row?.identidadKey,
    nombre: row?.identidadNombre || row?.nombreVisibleConversacion,
    rol: row?.identidadRol || row?.rolVisibleConversacion,
  });
  return {
    _id: id,
    contactoId: id,
    nombre: name,
    email: typeof row?.email === "string" ? row.email : "",
    rol: role,
    rolGlobal: role,
    unreadCount: count,
    enLinea: false,
    isUnreadPlaceholder: true,
    identidadConversacion: identity,
    identidadKey: identity.key,
    nombreVisibleConversacion: identity.nombre || name,
    rolVisibleConversacion: identity.rol || role,
  };
}

function formatVisibleSenderLabel(remitenteVisible) {
  if (!remitenteVisible || typeof remitenteVisible !== "object") {
    return "";
  }
  const nombre = normalizeVisibleSenderText(remitenteVisible.nombre);
  const rol = normalizeVisibleSenderText(remitenteVisible.rol);
  return [nombre, rol].filter(Boolean).join(" · ");
}

function findLatestIncomingVisibleSender(messages, currentUserId) {
  const currentId = normalizeMongoId(currentUserId);
  if (!currentId || !Array.isArray(messages)) {
    return null;
  }
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index];
    const senderId = normalizeMongoId(message?.remitenteId);
    if (!senderId || senderId === currentId) {
      continue;
    }
    const nombre = normalizeVisibleSenderText(message?.remitenteVisible?.nombre);
    const rol = normalizeVisibleSenderText(message?.remitenteVisible?.rol);
    if (nombre || rol) {
      return { nombre, rol };
    }
  }
  return null;
}

function getMessageUserId(message, keys) {
  if (!message || typeof message !== "object") {
    return "";
  }
  for (const key of keys) {
    const id = contactoId(message[key] ?? null);
    if (id) {
      return id;
    }
  }
  return "";
}

function isMessageFromCurrentUser(message, currentUserId) {
  const currentId = contactoId(currentUserId);
  if (!currentId) {
    return false;
  }
  const senderId = getMessageUserId(message, ["remitenteId", "remitente", "fromUserId", "senderId"]);
  if (senderId) {
    return senderId === currentId;
  }
  const recipientId = getMessageUserId(message, ["destinatarioId", "destinatario", "toUserId", "receiverId"]);
  return recipientId ? recipientId !== currentId : false;
}

function withStudentVisibleSender(contacto, messages, currentUserRole, currentUserId) {
  if (currentUserRole !== "alumno" || !contacto || typeof contacto !== "object") {
    return contacto;
  }
  const visibleSender = findLatestIncomingVisibleSender(messages, currentUserId);
  if (!visibleSender) {
    return contacto;
  }
  return {
    ...contacto,
    nombreVisibleConversacion: visibleSender.nombre || contacto.nombre || "",
    rolVisibleConversacion: visibleSender.rol || contactRoleLabelForDisplay(contacto) || "",
  };
}

// Componente de punto de notificación (Requirement #2)
const NotificationBadge = ({ count }) => {
  const [visible, setVisible] = useState(false);
  const label = count === 1 ? "1 mensaje no leído" : `${count} mensajes no leídos`;

  useEffect(() => {
    if (count > 0) {
      setVisible(true);
    } else {
      const timer = setTimeout(() => setVisible(false), 200); // Wait for fade out
      return () => clearTimeout(timer);
    }
  }, [count]);

  if (!visible && count <= 0) return null;

  return (
    <output
      className={`notification-dot ${count > 0 ? 'visible' : ''}`}
      aria-label={label}
    >
      {count > 99 ? "99+" : count}
    </output>
  );
};

NotificationBadge.propTypes = {
  count: PropTypes.number,
};

const MensajeriaInterna = () => {
  const location = useLocation();
  const lastDeepKeyRef = useRef("");
  const navigate = useNavigate();
  const [busqueda, setBusqueda] = useState("");
  const [contactRoleFilter, setContactRoleFilter] = useState("all");
  const [busquedaInicioChat, setBusquedaInicioChat] = useState("");
  const [resultadosInicioChat, setResultadosInicioChat] = useState([]);
  const [cargandoBusquedaInicio, setCargandoBusquedaInicio] = useState(false);
  const [errorBusquedaInicio, setErrorBusquedaInicio] = useState("");
  const [conversacionActiva, setConversacionActiva] = useState(null);
  const [mensaje, setMensaje] = useState("");
  const [visibleSenderName, setVisibleSenderName] = useState("");
  const [visibleSenderRole, setVisibleSenderRole] = useState("");
  const [visibleSenderSaving, setVisibleSenderSaving] = useState(false);
  const [visibleSenderStatus, setVisibleSenderStatus] = useState("");
  const conversacionesRef = useRef({}); // { "<cursoId>-<usuarioId>": [...mensajes] }
  const [mensajesChat, setMensajesChat] = useState([]);
  const [chatLoading, setChatLoading] = useState(false);
  const chatContainerRef = useRef(null);
  const borradasRef = useRef(new Map()); // key -> timestamp (ms)

  // 2) Manejador de tecla Enter para enviar mensaje
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (canChat) handleEnviarMensaje();
      // else setShowIntegrarmeModal(true); // Eliminado: Modal no existe
    }
  };

  const [mobileView, setMobileView] = useState("list");

  useEffect(() => {
    if (conversacionActiva) {
      setMobileView("chat");
    } else {
      setMobileView("list");
    }
  }, [conversacionActiva]);

  const handleBackToContacts = () => {
    setConversacionActiva(null);
    setMobileView("list");
    // Also clear URL params if needed
    const params = new URLSearchParams(location.search);
    params.delete("contacto");
    navigate({ search: params.toString() }, { replace: false });
  };

  const [autoScroll, setAutoScroll] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [allLoaded, setAllLoaded] = useState(false);

  // 1 Resetea autoScroll a true cada vez que cambias de conversación
  useEffect(() => {
    setAutoScroll(true);
  }, [conversacionActiva]);

  // 2 Hace scroll si autoScroll está activo
  useEffect(() => {
    if (autoScroll && typeof chatContainerRef.current?.scrollTo === "function") {
      chatContainerRef.current.scrollTo({
        top: chatContainerRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [mensajesChat, autoScroll]);

 
  const {
    cargarContactos,
    contactos,
    setContactos,
    setFiltro,
    messagingPermission,
    unreadByContact,
    contactosFiltroActual,
    contactosLoading,
    acknowledgeUnreadForContact,
  } = useContext(MensajesContext);
  const contactosRef = useRef(contactos);
  useEffect(() => {
    contactosRef.current = contactos;
  }, [contactos]);

  const studentContext = useStudent();
  const activeCourse = studentContext?.activeCourse ?? null; // curso activo del Campus (default en mensajería = mismo que campus)
  const [cursoSeleccionado, setCursoSeleccionado] = useState("administradores");
  const [directorScope, setDirectorScope] = useState(MESSAGING_DIRECTOR_SCOPE_USERS);
  const cursoSeleccionadoRef = useRef(cursoSeleccionado);
  const refreshContactosDebounceRef = useRef(null);
  const contactosCargaEnCursoRef = useRef(false);
  const [missingUnreadPartnerForRefresh, setMissingUnreadPartnerForRefresh] = useState(false);
  useEffect(() => {
    cursoSeleccionadoRef.current = cursoSeleccionado;
  }, [cursoSeleccionado]);

  const [idUsuario, setIdUsuario] = useState("");
  const [isUserResolved, setIsUserResolved] = useState(false);

  useEffect(() => {
    fetchSessionStatus()
      .then((session) => {
        if (session.authenticated) {
          setIdUsuario(contactoId(session.usuario));
        } else {
          setIdUsuario("");
        }
      })
      .catch((err) => {
        console.error("Error obteniendo usuario:", err);
        setIdUsuario("");
      })
      .finally(() => setIsUserResolved(true));
  }, []);

  useEffect(() => {
    if (messagingPermission !== true) {
      return undefined;
    }
    const sendHeartbeat = () => {
      fetchSessionStatus(undefined, { force: true }).catch(() => {
        /* ignore */
      });
    };
    sendHeartbeat();
    const heartbeatId = window.setInterval(sendHeartbeat, MESSAGING_PRESENCE_REFRESH_MS);
    return () => window.clearInterval(heartbeatId);
  }, [messagingPermission]);

  useEffect(() => {
    if (messagingPermission !== true) {
      return undefined;
    }
    const filtro = String(cursoSeleccionado ?? "").trim();
    if (filtro === "") {
      return undefined;
    }
    const refreshPresence = () => {
      cargarContactos(filtro);
    };
    const refreshId = window.setInterval(refreshPresence, MESSAGING_PRESENCE_REFRESH_MS);
    return () => window.clearInterval(refreshId);
  }, [messagingPermission, cursoSeleccionado, cargarContactos]);

  const [isEnrolled, setIsEnrolled] = useState(false);
  const [usuarioInfo, setUsuarioInfo] = useState({
    rol: "",
    cursosInscritos: [],
    tenantId: null,
  });
  const [cursosAsignados, setCursosAsignados] = useState([]);
  const [cargandoCursosAsignados, setCargandoCursosAsignados] = useState(false);

  // Unread counts bucketed by director messaging scope, keyed by the stored cursoId value
  // ("usuarios" / "administradores" / a real course id) so the "Tipo de destinatario"
  // dropdown can flag scopes with pending messages even when they are not selected.
  const directorScopeUnreadCounts = useMemo(() => {
    const counts = {
      [MESSAGING_DIRECTOR_SCOPE_USERS]: 0,
      [MESSAGING_DIRECTOR_SCOPE_ADMINS]: 0,
      [MESSAGING_DIRECTOR_SCOPE_COURSES]: 0,
    };
    if (usuarioInfo.rol !== "director" || !Array.isArray(unreadByContact)) {
      return counts;
    }
    unreadByContact.forEach((row) => {
      const count = Number(row?.count) || 0;
      if (count <= 0) return;
      const rowCursoId = typeof row?.cursoId === "string" ? row.cursoId : "";
      if (rowCursoId === MESSAGING_DIRECTOR_SCOPE_USERS) {
        counts[MESSAGING_DIRECTOR_SCOPE_USERS] += count;
      } else if (rowCursoId === MESSAGING_STAFF_SCOPE_ADMIN) {
        counts[MESSAGING_DIRECTOR_SCOPE_ADMINS] += count;
      } else if (rowCursoId) {
        counts[MESSAGING_DIRECTOR_SCOPE_COURSES] += count;
      }
    });
    return counts;
  }, [usuarioInfo.rol, unreadByContact]);

  const hasUnreadOutsideDirectorScope = usuarioInfo.rol === "director" && Object.entries(directorScopeUnreadCounts)
    .some(([scope, count]) => scope !== directorScope && count > 0);

  useEffect(() => {
    if (usuarioInfo.rol !== "director" && !isInstructorMessagingRole(usuarioInfo.rol)) {
      return undefined;
    }
    const controller = new AbortController();
    fetch(`${API_URL}/mensajes/identidad-visible`, {
      credentials: "include",
      signal: controller.signal,
    })
      .then(async (response) => {
        if (!response.ok) throw new Error("No se pudo cargar la identidad visible.");
        return response.json();
      })
      .then((identity) => {
        setVisibleSenderName(identity?.nombre || "");
        setVisibleSenderRole(identity?.rol || "");
      })
      .catch((error) => {
        if (error.name !== "AbortError") setVisibleSenderStatus(error.message);
      });
    return () => controller.abort();
  }, [usuarioInfo.rol]);

  const contactosCanonicos = useMemo(() => {
    if (!isUserResolved) return [];
    const list = Array.isArray(contactos) ? contactos : [];
    const myId = contactoId(idUsuario || null);
    const seen = new Set();
    const out = [];
    for (let i = 0; i < list.length; i += 1) {
      const c = list[i];
      const cid = contactoId(c);
      if (!cid) continue;
      const rowKey = conversationRowKey(c) || cid;
      if (seen.has(rowKey)) continue;
      seen.add(rowKey);
      if (myId && cid === myId) continue;
      out.push(c);
    }
    return out;
  }, [contactos, idUsuario, isUserResolved]);

  const contactosParaLista = useMemo(() => {
    if (!usuarioInfo.rol) {
      return [];
    }
    if (
      contactosLoading ||
      (String(contactosFiltroActual || "").trim() !== "" &&
        String(contactosFiltroActual) !== String(cursoSeleccionado || ""))
    ) {
      return [];
    }
    const orderUnreadFirst = (list) => [...list].sort(
      (a, b) => (Number(b.unreadCount) || 0) - (Number(a.unreadCount) || 0),
    );
    const byRole = filterMessagingContactsByRole(contactosCanonicos, contactRoleFilter);
    const q = (busqueda || "").trim().toLowerCase();
    if (!q) return orderUnreadFirst(byRole);
    return orderUnreadFirst(byRole.filter(
      (c) =>
        formatMessagingContactLabel(c).toLowerCase().includes(q) ||
        (c.nombre || "").toLowerCase().includes(q) ||
        (c.nombreVisibleConversacion || "").toLowerCase().includes(q) ||
        (c.rolVisibleConversacion || "").toLowerCase().includes(q) ||
        String(c.rolCurso || c.rolGlobal || c.rol || "")
          .toLowerCase()
          .includes(q)
    ));
  }, [contactosCanonicos, busqueda, contactRoleFilter, contactosFiltroActual, contactosLoading, cursoSeleccionado, usuarioInfo.rol]);

  const resultadosInicioFiltrados = useMemo(() => {
    const currentIdentity = buildConversationIdentity({
      nombre: visibleSenderName,
      rol: visibleSenderRole,
    });
    const activeKeys = new Set(
      contactosCanonicos.map((c) => conversationRowKey({
        ...c,
        identidadConversacion: identityFromContact(c),
      }))
    );
    const byRole = filterMessagingContactsByRole(resultadosInicioChat, contactRoleFilter);
    return byRole.filter((r) => {
      if (!r) return false;
      const candidateKey = conversationRowKey({
        ...r,
        identidadConversacion: currentIdentity,
        identidadKey: currentIdentity.key,
      });
      return !activeKeys.has(candidateKey);
    });
  }, [resultadosInicioChat, contactosCanonicos, contactRoleFilter, visibleSenderName, visibleSenderRole]);

  useEffect(() => {
    if (messagingPermission !== true) return;
    const filtro = String(cursoSeleccionado ?? "").trim();
    if (filtro === "") {
      setResultadosInicioChat([]);
      setCargandoBusquedaInicio(false);
      setErrorBusquedaInicio("");
      return;
    }
    const q = busquedaInicioChat.trim();
    if (q.length < 2) {
      setResultadosInicioChat([]);
      setCargandoBusquedaInicio(false);
      setErrorBusquedaInicio("");
      return;
    }
    let cancelled = false;
    setErrorBusquedaInicio("");
    setCargandoBusquedaInicio(true);
    const handle = window.setTimeout(() => {
      if (cancelled) return;
      const run = async () => {
        try {
          const url = `${API_URL}/mensajes/contactos/buscar?filtro=${encodeURIComponent(
            filtro
          )}&q=${encodeURIComponent(q)}`;
          const res = await fetch(url, { credentials: "include" });
          if (cancelled) return;
          if (!res.ok) {
            setErrorBusquedaInicio(es.messaging.startChatError);
            setResultadosInicioChat([]);
            return;
          }
          const data = await res.json();
          setResultadosInicioChat(Array.isArray(data) ? data : []);
        } catch {
          if (!cancelled) {
            setErrorBusquedaInicio(es.messaging.startChatError);
            setResultadosInicioChat([]);
          }
        } finally {
          if (!cancelled) {
            setCargandoBusquedaInicio(false);
          }
        }
      };
      void run();
    }, 350);
    return () => {
      cancelled = true;
      window.clearTimeout(handle);
      setCargandoBusquedaInicio(false);
    };
  }, [busquedaInicioChat, cursoSeleccionado, messagingPermission]);

  const handleCursoChange = useCallback((nuevoCursoId) => {
    if (!nuevoCursoId) return;

    setConversacionActiva(null);
    setMensajesChat([]);

    setCursoSeleccionado(nuevoCursoId);
    setFiltro(nuevoCursoId);
    if (isInstructorMessagingRole(usuarioInfo.rol)) {
      setContactRoleFilter(resolveInstructorContactRoleFilter(nuevoCursoId));
    }
    if (usuarioInfo.rol === "director") {
      setDirectorScope(MESSAGING_DIRECTOR_SCOPE_COURSES);
      setContactRoleFilter(resolveDirectorContactRoleFilter(MESSAGING_DIRECTOR_SCOPE_COURSES));
    }
    setBusquedaInicioChat("");
    setResultadosInicioChat([]);
    setErrorBusquedaInicio("");

    const params = new URLSearchParams(location.search);
    params.set("curso", nuevoCursoId);
    if (usuarioInfo.rol === "director") {
      params.set("scope", MESSAGING_DIRECTOR_SCOPE_COURSES);
    }
    params.delete("contacto");
    navigate({ search: params.toString() }, { replace: false });

    cargarContactos(nuevoCursoId);
  }, [location.search, navigate, cargarContactos, setFiltro, usuarioInfo.rol]);

  const handleDirectorScopeChange = useCallback((nuevoScope) => {
    const normalizedScope = [
      MESSAGING_DIRECTOR_SCOPE_USERS,
      MESSAGING_DIRECTOR_SCOPE_ADMINS,
      MESSAGING_DIRECTOR_SCOPE_COURSES,
    ].includes(nuevoScope)
      ? nuevoScope
      : MESSAGING_DIRECTOR_SCOPE_USERS;
    const defaultCourseId = pickInstructorDefaultCursoId(cursosAsignados, {
      urlCursoId: cursoSeleccionado,
      activeCourseId: activeCourse?._id,
    });
    const nextCurso = resolveDirectorContactScope(normalizedScope, defaultCourseId);

    setDirectorScope(normalizedScope);
    setContactRoleFilter(resolveDirectorContactRoleFilter(normalizedScope));
    setConversacionActiva(null);
    setMensajesChat([]);
    setBusquedaInicioChat("");
    setResultadosInicioChat([]);
    setErrorBusquedaInicio("");
    if (nextCurso) {
      setCursoSeleccionado(nextCurso);
      setFiltro(nextCurso);
    }

    const params = new URLSearchParams(location.search);
    if (nextCurso) {
      params.set("curso", nextCurso);
    } else {
      params.delete("curso");
    }
    params.set("scope", normalizedScope);
    params.delete("contacto");
    navigate({ search: params.toString() }, { replace: false });

    if (nextCurso) {
      cargarContactos(nextCurso);
    }
  }, [
    activeCourse?._id,
    cargarContactos,
    cursosAsignados,
    cursoSeleccionado,
    location.search,
    navigate,
    setFiltro,
  ]);

  useEffect(() => {
    const onCourseChanged = (e) => {
      const { courseId } = e.detail || {};
      if (courseId && courseId !== cursoSeleccionado) {
        handleCursoChange(courseId);
      }
    };

    window.addEventListener("courseChanged", onCourseChanged);
    return () => window.removeEventListener("courseChanged", onCourseChanged);
  }, [cursoSeleccionado, handleCursoChange]);

  const handleBackToAppHome = useCallback(() => {
    const r =
      usuarioInfo.rol && typeof usuarioInfo.rol === "string" && usuarioInfo.rol.trim() !== ""
        ? usuarioInfo.rol
        : "alumno";
    navigate(getHomeRouteByRole(r));
  }, [usuarioInfo.rol, navigate]);

  // Sincroniza curso con URL / campus y carga contactos cuando ya hay permiso de mensajería.
  // Importante: cargarContactos() del contexto no hace nada hasta messagingPermission === true
  // (ver MensajesContext). Si solo dependíamos de usuarioInfo/URL, el primer run podía ser antes
  // del verificar-token y entonces nunca se repetía → lista vacía hasta recargar la pestaña.
  useEffect(() => {
    const paramsLog = new URLSearchParams(location.search);
    const cursoQPLog = paramsLog.get("curso");
    let cursoResueltoLog = cursoQPLog;
    if (!cursoResueltoLog && usuarioInfo.rol === "alumno" && usuarioInfo.cursosInscritos.length > 0) {
      cursoResueltoLog = pickAlumnoDefaultCursoId(
        usuarioInfo.cursosInscritos,
        activeCourse,
        usuarioInfo.tenantId
      );
    }
    if (!cursoResueltoLog && usuarioInfo.rol === "director") {
      cursoResueltoLog = MESSAGING_DIRECTOR_SCOPE_USERS;
    }
    if (!cursoResueltoLog && isInstructorMessagingRole(usuarioInfo.rol) && cursosAsignados.length > 0) {
      cursoResueltoLog = pickInstructorDefaultCursoId(cursosAsignados, {
        urlCursoId: cursoQPLog,
        activeCourseId: activeCourse?._id,
      });
    }
    const sinRol = !usuarioInfo.rol;
    const sinPermisoMensajeria = messagingPermission !== true;
    if (isMensajeriaInternaDevLog) {
      console.log("[MensajeriaInterna] efecto carga contactos", {
        locationPathname: location.pathname,
        locationSearch: location.search,
        messagingPermission,
        "usuarioInfo.rol": usuarioInfo.rol || "(vacío — bloquea hasta cargar perfil)",
        curso: cursoResueltoLog ?? "(no resuelto: sin ?curso= y/o sin inscripciones)",
        bloqueos: {
          sinRol,
          sinPermisoMensajeria,
          sinCursoUsuario:
            usuarioInfo.rol === "alumno" &&
            !cursoQPLog &&
            usuarioInfo.cursosInscritos.length === 0,
        },
        llamaraACargarContactos: !sinRol && !sinPermisoMensajeria,
      });
    }

    if (!usuarioInfo.rol) return;
    if (messagingPermission !== true) return;
    if (isInstructorMessagingRole(usuarioInfo.rol) && cargandoCursosAsignados) return;

    const params = new URLSearchParams(location.search);
    const cursoQP = params.get("curso");
    const contactoQP = params.get("contacto");
    const directorScopeQP = normalizeDirectorScopeParam(params.get("scope"));

    if (!cursoQP && contactoQP) {
      if (usuarioInfo.rol === "director") {
        const nextDirectorScope = directorScopeQP || MESSAGING_DIRECTOR_SCOPE_USERS;
        const defaultCourseId = pickInstructorDefaultCursoId(cursosAsignados, {
          activeCourseId: activeCourse?._id,
        });
        const nextCurso = resolveDirectorContactScope(nextDirectorScope, defaultCourseId);
        setDirectorScope(nextDirectorScope);
        setContactRoleFilter(resolveDirectorContactRoleFilter(nextDirectorScope));
        if (nextCurso) {
          setCursoSeleccionado((prev) => (prev === nextCurso ? prev : nextCurso));
          setFiltro(nextCurso);
          cargarContactos(nextCurso);
        }
        return;
      }
      const currentScope = String(cursoSeleccionado || "").trim();
      if (currentScope) {
        setFiltro(currentScope);
        cargarContactos(currentScope);
      }
      return;
    }

    if (!cursoQP) {
      if (usuarioInfo.rol === "alumno") {
        if (usuarioInfo.cursosInscritos.length > 0) {
          const defaultCurso = pickAlumnoDefaultCursoId(
            usuarioInfo.cursosInscritos,
            activeCourse,
            usuarioInfo.tenantId
          );
          if (defaultCurso) {
            setCursoSeleccionado((prev) => (prev === defaultCurso ? prev : defaultCurso));
            setFiltro(defaultCurso);
            cargarContactos(defaultCurso);
          }
        }
      } else if (isInstructorMessagingRole(usuarioInfo.rol)) {
        const defaultCurso = pickInstructorDefaultCursoId(cursosAsignados, {
          activeCourseId: activeCourse?._id,
        });
        if (defaultCurso) {
          setCursoSeleccionado((prev) => (prev === defaultCurso ? prev : defaultCurso));
          setFiltro(defaultCurso);
          cargarContactos(defaultCurso);
        } else {
          setCursoSeleccionado((prev) =>
            prev === MESSAGING_STAFF_SCOPE_ADMIN ? prev : MESSAGING_STAFF_SCOPE_ADMIN,
          );
          setFiltro(MESSAGING_STAFF_SCOPE_ADMIN);
          cargarContactos(MESSAGING_STAFF_SCOPE_ADMIN);
        }
      } else if (usuarioInfo.rol === "director") {
        const nextDirectorScope = directorScopeQP || MESSAGING_DIRECTOR_SCOPE_USERS;
        const defaultCourseId = pickInstructorDefaultCursoId(cursosAsignados, {
          activeCourseId: activeCourse?._id,
        });
        const nextCurso = resolveDirectorContactScope(nextDirectorScope, defaultCourseId);
        setDirectorScope(nextDirectorScope);
        setContactRoleFilter(resolveDirectorContactRoleFilter(nextDirectorScope));
        if (nextCurso) {
          setCursoSeleccionado((prev) =>
            prev === nextCurso ? prev : nextCurso,
          );
          setFiltro(nextCurso);
          cargarContactos(nextCurso);
        }
      } else if (usuarioInfo.rol) {
        setCursoSeleccionado("");
        setFiltro("");
        setContactos([]);
      }
      return;
    }

    if (usuarioInfo.rol === "director") {
      const nextDirectorScope =
        directorScopeQP ||
        (isMessagingCourseScope(cursoQP)
          ? MESSAGING_DIRECTOR_SCOPE_COURSES
          : MESSAGING_DIRECTOR_SCOPE_USERS);
      setDirectorScope(nextDirectorScope);
      setContactRoleFilter(resolveDirectorContactRoleFilter(nextDirectorScope));
    }
    setCursoSeleccionado((prev) => (prev === cursoQP ? prev : cursoQP));
    setFiltro(cursoQP);
    cargarContactos(cursoQP);
  }, [
    location.pathname,
    location.search,
    setFiltro,
    setContactos,
    cargarContactos,
    usuarioInfo.rol,
    usuarioInfo.cursosInscritos,
    usuarioInfo.tenantId,
    activeCourse,
    messagingPermission,
    cursosAsignados,
    cargandoCursosAsignados,
    cursoSeleccionado,
  ]);

  useEffect(() => {
    if (!isInstructorMessagingRole(usuarioInfo.rol)) return;
    setContactRoleFilter(resolveInstructorContactRoleFilter(cursoSeleccionado));
  }, [usuarioInfo.rol, cursoSeleccionado]);

  useEffect(() => {
    if (usuarioInfo.rol !== "director") return;
    setContactRoleFilter(resolveDirectorContactRoleFilter(directorScope));
  }, [usuarioInfo.rol, directorScope]);

  useEffect(() => {
    lastDeepKeyRef.current = "";
  }, [location.search]);

  const cargarCursos = useCallback(async () => {
    setCargandoCursosAsignados(true);
    try {
      let infoUsuario = { rol: null, cursosInscritos: [], tenantId: null };
      const dataPerfil = await getCurrentUserProfile();
      if (dataPerfil) {
        infoUsuario = {
          rol: dataPerfil.rol,
          cursosInscritos: perfilCursosToIdList(dataPerfil.cursosInscritos),
          tenantId: dataPerfil.tenantId ?? null,
        };
      }

      let cursosAsignadosLista = [];
      if (isInstructorMessagingRole(infoUsuario.rol) || infoUsuario.rol === "director") {
        const fromPerfil = mapPerfilCursosToBasicList(dataPerfil?.cursosInscritos);
        const resLista = await fetch(`${API_URL}/cursos/lista-basica`, {
          credentials: "include",
        });
        if (resLista.ok) {
          const lista = await resLista.json();
          cursosAsignadosLista = Array.isArray(lista) ? lista : [];
        } else {
          console.error("Error al cargar cursos asignados, status:", resLista.status);
          cursosAsignadosLista = fromPerfil;
        }
      }

      setUsuarioInfo(infoUsuario);
      if (infoUsuario.rol === "alumno") {
        setContactos([]);
      }
      setCursosAsignados(cursosAsignadosLista);
    } catch (err) {
      console.error("Error crítico en cargarCursos:", err);
      setUsuarioInfo({ rol: null, cursosInscritos: [], tenantId: null });
      setCursosAsignados([]);
    } finally {
      setCargandoCursosAsignados(false);
    }
  }, [setContactos]);

  // ————————————————
  // 1) Cargar lista de cursos públicos
  // ————————————————
  useEffect(() => {
    cargarCursos();
  }, [cargarCursos]);

  // ————————————————
  // 2) Comprobar si el usuario está inscrito cuando cambie cursoSeleccionado
  // ————————————————
  useEffect(() => {
    if (
      !cursoSeleccionado ||
      cursoSeleccionado === MESSAGING_STAFF_SCOPE_ADMIN ||
      cursoSeleccionado === MESSAGING_DIRECTOR_SCOPE_USERS ||
      cursoSeleccionado === "jefes-direccion"
    ) {
      setIsEnrolled(false);
      return;
    }

    if (isInstructorMessagingRole(usuarioInfo.rol)) {
      setIsEnrolled(instructorHasAssignedCourse(cursoSeleccionado, cursosAsignados));
      return;
    }

    if (usuarioInfo.rol === "director" && isMessagingCourseScope(cursoSeleccionado)) {
      setIsEnrolled(true);
      return;
    }

    fetch(`${API_URL}/perfil`, {
      credentials: "include",
    })
      .then((res) => {
        if (!res.ok) throw new Error("No pude cargar tu perfil");
        return res.json();
      })
      .then((perfilData) => {
        const enrolledIds = perfilCursosToIdList(perfilData.cursosInscritos);
        setIsEnrolled(enrolledIds.includes(String(cursoSeleccionado)));
      })
      .catch(() => setIsEnrolled(false));
  }, [cursoSeleccionado, usuarioInfo.rol, cursosAsignados]);


  // 6) Auxiliar: función para recargar la conversación activa
  const formatRol = (rol) => {
    const s =
      typeof rol === "string"
        ? rol.trim()
        : rol != null && (typeof rol === "number" || typeof rol === "boolean")
          ? String(rol)
          : "";
    if (!s) return "";
    const label = getMessagingRoleLabel(s);
    if (label !== s) return label;
    return s.charAt(0).toUpperCase() + s.slice(1);
  };

  const cargarConversacion = useCallback(async (otherUserId, cursoId, options = {}) => {
    const contactoNorm = normalizeMongoId(otherUserId);
    const cursoNorm = normalizeMongoId(cursoId);
    if (!contactoNorm) return [];
    const { before, after, identidad } = options;
    const identityKey = identidad == null ? "" : String(identidad);

    try {
      const cursoQuery = cursoNorm || "null";
      let url = `${API_URL}/mensajes/conversacion?contacto=${encodeURIComponent(contactoNorm)}&cursoId=${encodeURIComponent(cursoQuery)}&limit=50&identidad=${encodeURIComponent(identityKey)}`;
      if (before) {
        const b = before instanceof Date ? before.toISOString() : before;
        url += `&before=${encodeURIComponent(b)}`;
      }
      if (after) {
        const a = after instanceof Date ? after.toISOString() : after;
        url += `&after=${encodeURIComponent(a)}`;
      }

      const res = await fetch(url, { credentials: "include" });

      if (res.status === 404) return [];
      if (!res.ok) {
        return null;
      }

      let data = await res.json().catch(() => null);
      if (!Array.isArray(data)) return null;
      
      const chatFormateado = data.map((m) => {
        const fecha = m.fechaEnvio || m.fecha || m.createdAt || m.created_at || null;
        const remitenteForId =
          m.remitente != null && typeof m.remitente === "object"
            ? m.remitente
            : m.remitenteId ?? m.remitente;
        const destinatarioForId =
          m.destinatario != null && typeof m.destinatario === "object"
            ? m.destinatario
            : m.destinatarioId ?? m.destinatario;
        return {
          remitenteId: contactoId(remitenteForId ?? null),
          destinatarioId: contactoId(destinatarioForId ?? null),
          texto: m.texto,
          _id: m._id,
          fecha,
          leido: m.leido,
          remitenteVisible: m.remitenteVisible,
        };
      });

      const cut = borradasRef.current.get(conversationCacheKey(cursoNorm, contactoNorm, identityKey)) ?? 0;
      const chatFiltrado = cut
        ? chatFormateado.filter((m) => {
            let ts = 0;
            if (m.fecha) ts = new Date(m.fecha).getTime();
            else if (typeof m._id === "string" && m._id.length >= 8) {
              ts = parseInt(m._id.slice(0, 8), 16) * 1000;
            }
            return ts > cut;
          })
        : chatFormateado;

      return chatFiltrado;
    } catch (err) {
      console.error("Error inesperado al cargar conversación", err);
      return null;
    }
  }, []);


  const loadMoreHistory = async () => {
    if (loadingMore || allLoaded || !conversacionActiva) return;
    setLoadingMore(true);

    const firstMsg = mensajesChat[0];
    const beforeDate = firstMsg ? firstMsg.fecha : null;
    
    if (!beforeDate) {
        setLoadingMore(false);
        return;
    }

    const beforeD = new Date(beforeDate);
    const beforeIso = Number.isNaN(beforeD.getTime()) ? null : beforeD.toISOString();
    if (!beforeIso) {
      setLoadingMore(false);
      return;
    }

    const container = chatContainerRef.current;
    const oldHeight = container ? container.scrollHeight : 0;
    const oldScrollTop = container ? container.scrollTop : 0;

    const oldMessages = await cargarConversacion(
      contactoId(conversacionActiva),
      cursoSeleccionado,
      { before: beforeIso, identidad: identityFromContact(conversacionActiva).key }
    );
    if (!Array.isArray(oldMessages)) {
      setLoadingMore(false);
      return;
    }
    
    if (oldMessages.length < 50) {
        setAllLoaded(true);
    }

    if (oldMessages.length > 0) {
        setMensajesChat(prev => {
             // Avoid duplicates
             const ids = new Set(prev.map(m => m._id));
             const uniqueOld = oldMessages.filter(m => !ids.has(m._id));
             return [...uniqueOld, ...prev];
        });
        
        // Adjust scroll after render
        setTimeout(() => {
             if (chatContainerRef.current) {
                 const newHeight = chatContainerRef.current.scrollHeight;
                 chatContainerRef.current.scrollTop = newHeight - oldHeight + oldScrollTop;
             }
        }, 0);
    }
    setLoadingMore(false);
  };

  // 7) Cuando el usuario selecciona un contacto, utilizamos cargarConversacion
  const handleSeleccionarContacto = useCallback(
    async (contacto, fromDeepLink = false) => {
      const cid = contactoId(contacto);
      if (!cid) return;
      const selectedIdentity = identityFromContact(contacto);
      const selectedRow = {
        ...contacto,
        unreadCount: Number(contacto.unreadCount) || 0,
        identidadConversacion: selectedIdentity,
        identidadKey: selectedIdentity.key,
      };
      const isSameClose = isSameConversation(conversacionActiva, selectedRow);

      if (isSameClose) {
        setMobileView("chat");
        return;
      }

      setContactos((prev) => {
        const list = Array.isArray(prev) ? prev : [];
        if (list.some((c) => isSameConversation(c, selectedRow))) return list;
        return [selectedRow, ...list];
      });
      const row =
        contactosCanonicos.find((x) => isSameConversation(x, selectedRow)) ||
        (contacto && contactoId(contacto) === cid ? selectedRow : null);
      if (!row) return;

      if (usuarioInfo.rol !== "alumno" && (selectedIdentity.nombre || selectedIdentity.rol)) {
        setVisibleSenderName(selectedIdentity.nombre);
        setVisibleSenderRole(selectedIdentity.rol);
      }

      setAutoScroll(true);
      setChatLoading(true);
      setMensajesChat([]);
      setConversacionActiva(row);
      setAllLoaded(false); // Reset pagination
      setLoadingMore(false);
      setBusquedaInicioChat("");
      setResultadosInicioChat([]);
      setErrorBusquedaInicio("");

      const key = conversationCacheKey(
        normalizeMongoId(cursoSeleccionado),
        cid,
        selectedIdentity.key
      );
      if (borradasRef.current.has(key)) {
        setMensajesChat([]);   // conversación borrada: mostrar vacía
        setChatLoading(false);
      }

      const params = new URLSearchParams(location.search);
      params.set("curso", cursoSeleccionado);
      if (usuarioInfo.rol === "director") {
        params.set("scope", directorScope);
      }
      params.set("contacto", cid);
      if (selectedIdentity.key) {
        params.set("identidad", selectedIdentity.key);
      } else {
        params.delete("identidad");
      }
      navigate({ search: params.toString() }, { replace: false });

      if (borradasRef.current.has(key)) {
        setMensajesChat([]);
        setChatLoading(false);
      } else if (conversacionesRef.current[key]) {
        setMensajesChat(conversacionesRef.current[key]);
        setChatLoading(false);
      } else {
        const msgs = await cargarConversacion(cid, cursoSeleccionado, {
          identidad: selectedIdentity.key,
        });
        if (Array.isArray(msgs)) {
          conversacionesRef.current[key] = msgs;
          setMensajesChat(msgs);
        } else {
          setMensajesChat([]);
        }
        setChatLoading(false);
      }

      const markReadResponse = await fetch(`${API_URL}/mensajes/marcar-leidos`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          contactoId: cid,
          cursoId: normalizeMongoId(cursoSeleccionado),
          identidad: selectedIdentity.key,
        }),
      });
      if (markReadResponse.ok && typeof acknowledgeUnreadForContact === "function") {
        acknowledgeUnreadForContact(cid);
      }

      await cargarContactos(cursoSeleccionado);

      setContactos((cs) =>
        cs.map((c) => (isSameConversation(c, selectedRow) ? { ...c, unreadCount: 0 } : c))
      );
      emitUnreadChanged();
    },
    [
      cursoSeleccionado,
      setContactos,
      cargarContactos,
      cargarConversacion,
      navigate,
      location.search,
      conversacionActiva,
      contactosCanonicos,
      acknowledgeUnreadForContact,
      directorScope,
      usuarioInfo.rol,
    ]
  );

  // Para comparar tamaños entre ticks
  const lastLenRef = useRef(0);

  // Reset cuando cambias de conversación o curso
  useEffect(() => {
    lastLenRef.current = 0;
  }, [conversacionActiva, cursoSeleccionado]);

  const mensajesChatRef = useRef(mensajesChat);
  useEffect(() => {
    mensajesChatRef.current = mensajesChat;
  }, [mensajesChat]);

  useEffect(() => {
    if (usuarioInfo.rol !== "alumno" || !conversacionActiva) {
      return;
    }
    const cid = contactoId(conversacionActiva);
    const updated = withStudentVisibleSender(conversacionActiva, mensajesChat, usuarioInfo.rol, idUsuario);
    if (
      updated === conversacionActiva ||
      !cid ||
      (
        updated.nombreVisibleConversacion === conversacionActiva.nombreVisibleConversacion &&
        updated.rolVisibleConversacion === conversacionActiva.rolVisibleConversacion
      )
    ) {
      return;
    }

    setConversacionActiva(updated);
    setContactos((prev) => {
      if (!Array.isArray(prev)) {
        return prev;
      }
      return prev.map((contacto) => (
        isSameConversation(contacto, conversacionActiva) ? { ...contacto, ...updated } : contacto
      ));
    });
  }, [conversacionActiva, idUsuario, mensajesChat, setContactos, usuarioInfo.rol]);

  // Polling cada 1.5s mientras haya conversación activa
  useEffect(() => {
    if (!conversacionActiva || !cursoSeleccionado) return;

    let cancelled = false;

    const tick = async () => {
      if (cancelled) return;

      const keyTick = conversationCacheKey(
        normalizeMongoId(cursoSeleccionado),
        contactoId(conversacionActiva),
        identityFromContact(conversacionActiva).key
      );
      if (borradasRef.current.has(keyTick)) {
        setMensajesChat([]);
        return;
      }

      const lastMsg = mensajesChatRef.current[mensajesChatRef.current.length - 1];
      let afterOpt = {};
      if (lastMsg?.fecha) {
        const d = new Date(lastMsg.fecha);
        if (!Number.isNaN(d.getTime())) {
          afterOpt = { after: d.toISOString() };
        }
      }

      const nuevos = await cargarConversacion(contactoId(conversacionActiva), cursoSeleccionado, {
        ...afterOpt,
        identidad: identityFromContact(conversacionActiva).key,
      });
      if (!Array.isArray(nuevos)) return;

      if (nuevos.length > 0) {
        try {
          const markReadResponse = await fetch(`${API_URL}/mensajes/marcar-leidos`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            credentials: "include",
            body: JSON.stringify({
              contactoId: contactoId(conversacionActiva),
              cursoId: normalizeMongoId(cursoSeleccionado),
              identidad: identityFromContact(conversacionActiva).key,
            }),
          });
          if (markReadResponse.ok && typeof acknowledgeUnreadForContact === "function") {
            acknowledgeUnreadForContact(contactoId(conversacionActiva));
          }
        } catch (e) {
          console.error(e);
        }
        
        setMensajesChat(prev => {
            const ids = new Set(prev.map(m => m._id));
            const uniqueNuevos = nuevos.filter(m => !ids.has(m._id));
            if (uniqueNuevos.length === 0) return prev;
            return [...prev, ...uniqueNuevos];
        });
        setAutoScroll(true);
      }

      await cargarContactos(cursoSeleccionado);
    };

    const id = setInterval(tick, 1500);
    return () => { cancelled = true; clearInterval(id); };
  }, [conversacionActiva, cursoSeleccionado, cargarContactos, cargarConversacion, acknowledgeUnreadForContact]);



  // Deep-link robusto
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const contactoQP = params.get("contacto");
    const cursoQP = params.get("curso");
    if (!contactoQP) return;
    const assignedCourseScopes = cursosAsignados
      .map((curso) => normalizeMongoId(curso?._id))
      .filter(Boolean);
    const candidateScopes = buildDeepLinkCandidateScopes({
      role: usuarioInfo.rol,
      cursoQP,
      cursoSeleccionado,
      directorScopeQP: normalizeDirectorScopeParam(params.get("scope")),
      assignedCourseScopes,
      enrolledCourseScopes: perfilCursosToIdList(usuarioInfo.cursosInscritos),
    });

    let cancelled = false;
    const tryDeepLink = async (attempt = 0) => {
      if (cancelled) return;
      const scopeIndex = Math.min(Math.floor(attempt / 5), Math.max(candidateScopes.length - 1, 0));
      const cursoDestino = candidateScopes[scopeIndex];
      if (!cursoDestino) {
        return setTimeout(() => tryDeepLink(attempt + 1), 120);
      }

      if (cursoSeleccionado !== cursoDestino) {
        if (usuarioInfo.rol === "director") {
          if (isMessagingCourseScope(cursoDestino)) {
            setDirectorScope(MESSAGING_DIRECTOR_SCOPE_COURSES);
          } else if (cursoDestino === MESSAGING_DIRECTOR_SCOPE_USERS) {
            setDirectorScope(MESSAGING_DIRECTOR_SCOPE_USERS);
          } else if (cursoDestino === MESSAGING_STAFF_SCOPE_ADMIN) {
            setDirectorScope(MESSAGING_DIRECTOR_SCOPE_ADMINS);
          }
          setContactRoleFilter(resolveDirectorContactRoleFilter(
            cursoDestino === MESSAGING_STAFF_SCOPE_ADMIN ? MESSAGING_DIRECTOR_SCOPE_ADMINS : cursoDestino,
          ));
        }
        setCursoSeleccionado(cursoDestino);
        setFiltro(cursoDestino);
        return setTimeout(() => tryDeepLink(attempt + 1), 120);
      }

      if (!contactos || String(contactosFiltroActual || "") !== cursoDestino) {
        await cargarContactos(cursoDestino);
        return setTimeout(() => tryDeepLink(attempt + 1), 120);
      }

      const identidadQP = params.get("identidad") || "";
      const c = contactosCanonicos.find((x) => {
        if (contactoId(x) !== contactoId(contactoQP)) return false;
        if (!identidadQP) return true;
        return identityFromContact(x).key === identidadQP;
      });
      const unreadRow = Array.isArray(unreadByContact)
        ? unreadByContact.find((row) => {
          if (contactoId(row?.contactoId) !== contactoId(contactoQP)) return false;
          if (!identidadQP) return true;
          return String(row?.identidadKey || "") === identidadQP;
        })
        : null;
      const fallbackRole =
        cursoDestino === MESSAGING_STAFF_SCOPE_ADMIN
          ? "director"
          : cursoDestino === MESSAGING_DIRECTOR_SCOPE_USERS
            ? "alumno"
            : "";
      const deepLinkedContact =
        c ||
        (Number(unreadRow?.count) > 0
          ? buildUnreadContactPlaceholder(unreadRow, fallbackRole)
          : null);
      if (deepLinkedContact) {
        const key = `${cursoDestino}-${contactoQP}-${identidadQP}`;
        if (lastDeepKeyRef.current !== key) {
          lastDeepKeyRef.current = key;
          await handleSeleccionarContacto(deepLinkedContact, true);
        }
        return;
      }

      if (attempt < Math.max(35, candidateScopes.length * 5)) {
        setTimeout(() => tryDeepLink(attempt + 1), 120);
      }
    };

    tryDeepLink();
    return () => {
      cancelled = true;
    };
  }, [
    location.search,
    contactos,
    contactosCanonicos,
    contactosFiltroActual,
    cursoSeleccionado,
    cursosAsignados,
    usuarioInfo.cursosInscritos,
    setFiltro,
    cargarContactos,
    handleSeleccionarContacto,
    unreadByContact,
    usuarioInfo.rol,
  ]);

  const canConfigureVisibleSender =
    (usuarioInfo.rol === "director" || isInstructorMessagingRole(usuarioInfo.rol)) &&
    isAlumnoMessagingContact(conversacionActiva);

  const buildVisibleSenderPayloadForSend = useCallback(() => {
    if (!canConfigureVisibleSender) {
      return null;
    }
    const nombre = normalizeVisibleSenderText(visibleSenderName);
    const rol = normalizeVisibleSenderText(visibleSenderRole);
    if (!nombre && !rol) {
      return null;
    }
    return { nombre, rol };
  }, [canConfigureVisibleSender, visibleSenderName, visibleSenderRole]);

  const openIdentityThread = useCallback(async (identity, { loadHistory = true } = {}) => {
    if (!conversacionActiva) {
      return null;
    }
    const nextConversation = decorateContactForThread(
      conversacionActiva,
      identity,
      { viewerIsStudent: usuarioInfo.rol === "alumno" },
    );
    if (isSameConversation(conversacionActiva, nextConversation)) {
      return nextConversation;
    }

    setContactos((prev) => {
      const list = Array.isArray(prev) ? prev : [];
      if (list.some((row) => isSameConversation(row, nextConversation))) {
        return list;
      }
      return [nextConversation, ...list];
    });
    setConversacionActiva(nextConversation);
    setMensajesChat([]);
    setChatLoading(loadHistory);
    setAllLoaded(false);

    const cid = contactoId(nextConversation);
    const params = new URLSearchParams(location.search);
    params.set("contacto", cid);
    if (nextConversation.identidadKey) {
      params.set("identidad", nextConversation.identidadKey);
    } else {
      params.delete("identidad");
    }
    navigate({ search: params.toString() }, { replace: true });

    if (!loadHistory || !cid) {
      setChatLoading(false);
      return nextConversation;
    }

    const history = await cargarConversacion(cid, cursoSeleccionado, {
      identidad: nextConversation.identidadKey,
    });
    if (Array.isArray(history)) {
      conversacionesRef.current[conversationCacheKey(
        normalizeMongoId(cursoSeleccionado),
        cid,
        nextConversation.identidadKey,
      )] = history;
      setMensajesChat(history);
    }
    setChatLoading(false);
    return nextConversation;
  }, [
    conversacionActiva,
    cursoSeleccionado,
    location.search,
    navigate,
    setContactos,
    usuarioInfo.rol,
    cargarConversacion,
  ]);

  const handleSaveVisibleSender = async () => {
    setVisibleSenderSaving(true);
    setVisibleSenderStatus("");
    try {
      const response = await fetch(`${API_URL}/mensajes/identidad-visible`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ nombre: visibleSenderName, rol: visibleSenderRole }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || "No se pudo guardar la identidad visible.");
      }
      const savedIdentity = buildConversationIdentity({
        nombre: data.nombre || visibleSenderName,
        rol: data.rol || visibleSenderRole,
      });
      setVisibleSenderName(savedIdentity.nombre);
      setVisibleSenderRole(savedIdentity.rol);
      setVisibleSenderStatus("Identidad guardada.");
      if (canConfigureVisibleSender) {
        await openIdentityThread(savedIdentity);
      }
    } catch (error) {
      setVisibleSenderStatus(error.message || "No se pudo guardar la identidad visible.");
    } finally {
      setVisibleSenderSaving(false);
    }
  };

  // 9) Enviar mensaje (igual que antes)
  const handleEnviarMensaje = async () => {
    if (!conversacionActiva || !mensaje.trim()) return;
    if (!canChat) {
      // setShowIntegrarmeModal(true); // Eliminado: Modal no existe
      return;
    }

    const visibleSender = buildVisibleSenderPayloadForSend();
    const threadIdentity = resolveOutgoingThreadIdentity({
      canConfigureVisibleSender,
      visibleSender,
      activeContact: conversacionActiva,
    });
    const targetConversation = await openIdentityThread(threadIdentity, { loadHistory: false });
    const conversationForSend = targetConversation || conversacionActiva;
    const staysOnOpenThread = isSameConversation(conversacionActiva, conversationForSend);

    const provisional = {
      remitenteId: contactoId(idUsuario || null),
      texto: mensaje.trim(),
      _id: Date.now(),
      remitenteVisible: visibleSender,
    };
    const sendCacheKey = conversationCacheKey(
      normalizeMongoId(cursoSeleccionado),
      contactoId(conversationForSend),
      threadIdentity.key,
    );
    setMensajesChat((prev) => (staysOnOpenThread ? [...prev, provisional] : [provisional]));
    conversacionesRef.current[sendCacheKey] = staysOnOpenThread
      ? [...(conversacionesRef.current[sendCacheKey] || []), provisional]
      : [provisional];
    setMensaje("");
    setAutoScroll(true);
    if (typeof chatContainerRef.current?.scrollTo === "function") {
      chatContainerRef.current.scrollTo({
        top: chatContainerRef.current.scrollHeight,
        behavior: "smooth",
      });
    }

    try {
      const payload = {
        destinatario: contactoId(conversationForSend),
        texto: provisional.texto,
        cursoId: normalizeMongoId(cursoSeleccionado),
        identidadHilo: threadIdentity,
      };
      if (provisional.remitenteVisible) {
        payload.remitenteVisible = provisional.remitenteVisible;
      }
      const res = await fetch(`${API_URL}/mensajes/enviar`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const raw = await res.text().catch(() => "");
        let errorMsg = "Error desconocido";

        try {
          const errData = raw ? JSON.parse(raw) : {};
          errorMsg = errData.error || raw || errorMsg;
        } catch {
          errorMsg = raw || errorMsg;
        }

        console.error("Error detallado:", raw);
        setMensajesChat((prev) => prev.filter((m) => m._id !== provisional._id));
        alert(`Error al enviar mensaje: ${errorMsg}`);
        return;
      }

     const { datos } = await res.json().catch(() => ({}));
     if (datos?._id) {
       const persisted = {
         ...provisional,
         _id: datos._id,
         fecha: datos.fechaEnvio,
         remitenteVisible: datos.remitenteVisible,
       };
       setMensajesChat(prev =>
         prev.map(m => (m._id === provisional._id ? persisted : m))
       );
       const cached = conversacionesRef.current[sendCacheKey] || [];
       conversacionesRef.current[sendCacheKey] = cached.some((m) => m._id === provisional._id)
         ? cached.map((m) => (m._id === provisional._id ? persisted : m))
         : [...cached, persisted];
     }
     await cargarContactos(cursoSeleccionado); // solo para badges
     setAutoScroll(true);
     emitUnreadChanged();
    } catch (err) {
      console.error(err);
      setMensajesChat((prev) => prev.filter((m) => m._id !== provisional._id));
      setMensaje(provisional.texto);
      alert("No se pudo enviar el mensaje. Revisa tu conexión e inténtalo de nuevo.");
    }
  };

  // Sólo los filtros de tipo ID de curso permiten integrarse
  const isCursoReal =
    cursoSeleccionado !== MESSAGING_STAFF_SCOPE_ADMIN &&
    cursoSeleccionado !== "jefes-direccion";

  const staffCanChatInCourse =
    usuarioInfo.rol === "director" ||
    (isInstructorMessagingRole(usuarioInfo.rol) &&
      instructorHasAssignedCourse(cursoSeleccionado, cursosAsignados));

  const canChat = !isCursoReal || isEnrolled || staffCanChatInCourse;

  // Renderizado de mensajes
  const renderMensajes = () => {
    if (!isUserResolved) return <div className="no-messages">Cargando conversación...</div>;
    if (chatLoading) return <div className="no-messages">Cargando conversación...</div>;
    const currentUserId = contactoId(idUsuario || null);

    if (!mensajesChat || mensajesChat.length === 0) {
      return <div className="no-messages">No hay mensajes en esta conversación.</div>;
    }

    return mensajesChat.map((m, i) => {
      const esYo = isMessageFromCurrentUser(m, currentUserId);
      const visibleSenderLabel =
        usuarioInfo.rol === "alumno" && !esYo
          ? formatVisibleSenderLabel(m.remitenteVisible)
          : "";

      let fechaStr = "";
      if (m.fecha) {
          const d = new Date(m.fecha);
          fechaStr = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
          const now = new Date();
          if (d.toDateString() !== now.toDateString()) {
              fechaStr = d.toLocaleDateString() + " " + fechaStr;
          }
      }

      return (
        <div key={m._id || i} className={esYo ? "mensaje mensaje--yo" : "mensaje mensaje--otro"}>
           {visibleSenderLabel ? (
             <div className="mensaje-visible-sender">{visibleSenderLabel}</div>
           ) : null}
           <div className="mensaje-texto">{m.texto}</div>
           <div className="mensaje-meta" style={{ fontSize: '0.7em', opacity: 0.7, marginTop: '4px', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '4px' }}>
               <span>{fechaStr}</span>
               {esYo && (
                   <span title={m.leido ? "Leído" : "Enviado"} style={{ color: m.leido ? '#4cd137' : 'inherit' }}>
                       {m.leido ? "✓✓" : "✓"}
                   </span>
               )}
           </div>
        </div>
      );
    });
  };



  // 10) Unread badges: driven by MensajesContext unified poll (no duplicate /sin-leer requests)
  useEffect(() => {
    if (!Array.isArray(unreadByContact)) return undefined;

    const unreadMap = new Map();
    const unreadRowsById = new Map();
    unreadByContact.forEach((item) => {
      const id = String(contactoId(item.contactoId));
      if (!id) return;
      const rowKey = conversationRowKey({
        _id: id,
        identidadKey: item.identidadKey,
        identidadConversacion: {
          key: item.identidadKey,
          nombre: item.identidadNombre,
          rol: item.identidadRol,
        },
      }) || id;
      unreadMap.set(rowKey, item.count);
      unreadRowsById.set(rowKey, item);
    });

    const prevList = Array.isArray(contactos) ? contactos : contactosRef.current;
    const knownIds = new Set((prevList ?? []).map((c) => conversationRowKey(c) || contactoId(c)));
    let missingUnreadPartner = false;
    for (const [rowKey, count] of unreadMap.entries()) {
      if ((count || 0) > 0 && !knownIds.has(rowKey)) {
        missingUnreadPartner = true;
        break;
      }
    }
    setMissingUnreadPartnerForRefresh(missingUnreadPartner);

    let fallbackPlaceholderRole = "";
    if (contactRoleFilter === "alumno") {
      fallbackPlaceholderRole = "alumno";
    } else if (contactRoleFilter === "staff") {
      fallbackPlaceholderRole = "director";
    }

    setContactos((prev) => {
      if (!prev) return prev;

      let changed = false;
      const updated = prev.map((c) => {
        const currentCount = c.unreadCount || 0;
        const newCount = unreadMap.get(conversationRowKey(c) || contactoId(c)) || 0;

        if (currentCount !== newCount) {
          changed = true;
          return { ...c, unreadCount: newCount };
        }
        return c;
      });

      const updatedIds = new Set(updated.map((c) => conversationRowKey(c) || contactoId(c)));
      const placeholders = [];
      unreadRowsById.forEach((row, rowKey) => {
        const count = Number(row?.count) || 0;
        if (count <= 0 || updatedIds.has(rowKey)) return;
        const placeholder = buildUnreadContactPlaceholder(row, fallbackPlaceholderRole);
        if (placeholder) {
          placeholders.push(placeholder);
          updatedIds.add(rowKey);
        }
      });

      if (placeholders.length > 0) {
        return [...placeholders, ...updated];
      }
      return changed ? updated : prev;
    });

    return undefined;
  }, [contactRoleFilter, contactos, unreadByContact, setContactos]);

  useEffect(() => {
    if (!missingUnreadPartnerForRefresh) {
      if (refreshContactosDebounceRef.current) {
        window.clearTimeout(refreshContactosDebounceRef.current);
        refreshContactosDebounceRef.current = null;
      }
      return;
    }
    if (refreshContactosDebounceRef.current) {
      window.clearTimeout(refreshContactosDebounceRef.current);
    }
    refreshContactosDebounceRef.current = window.setTimeout(() => {
      refreshContactosDebounceRef.current = null;
      void (async () => {
        if (contactosCargaEnCursoRef.current) {
          setMissingUnreadPartnerForRefresh(false);
          return;
        }
        contactosCargaEnCursoRef.current = true;
        try {
          await cargarContactos(cursoSeleccionadoRef.current);
        } finally {
          contactosCargaEnCursoRef.current = false;
          setMissingUnreadPartnerForRefresh(false);
        }
      })();
    }, 400);
    return () => {
      if (refreshContactosDebounceRef.current) {
        window.clearTimeout(refreshContactosDebounceRef.current);
        refreshContactosDebounceRef.current = null;
      }
    };
  }, [missingUnreadPartnerForRefresh, contactos, cargarContactos]);

  if (isMensajeriaInternaDevLog) {
    console.log("Renderizando contactos en UI:", contactos, "→ lista filtrada:", contactosParaLista);
  }

  if (!isUserResolved || !usuarioInfo.rol) {
    return (
      <div className="mi-container mi-container--loading">
        <div className="mi-loading-state" role="status" aria-live="polite">
          <span className="mi-loading-state__spinner" aria-hidden="true" />
          <p>Cargando mensajería…</p>
        </div>
      </div>
    );
  }

  const latestVisibleSender = usuarioInfo.rol === "alumno"
    ? findLatestIncomingVisibleSender(mensajesChat, idUsuario)
    : null;
  const highlightedUnreadContactId = contactoId(new URLSearchParams(location.search).get("contacto"));
  const activeThreadIdentity = identityFromContact(conversacionActiva);
  const activeConversationName =
    latestVisibleSender?.nombre || conversacionActiva?.nombreVisibleConversacion || conversacionActiva?.nombre || "";
  const activeStaffIdentityLabel = formatConversationIdentityLabel(es.messaging.contactIdentityLabel, {
    name: activeThreadIdentity.nombre,
    role: activeThreadIdentity.rol,
  });
  const activeConversationRole =
    latestVisibleSender?.rol ||
    (usuarioInfo.rol === "alumno"
      ? conversacionActiva?.rolVisibleConversacion
      : activeStaffIdentityLabel) ||
    conversacionActiva?.rolVisibleConversacion ||
    formatRol(contactRoleLabelForDisplay(conversacionActiva)) ||
    "—";

  return (
    <div className="mi-container">
      <div className={`mi-chat-layout ${mobileView === 'chat' ? 'mobile-show-chat' : 'mobile-show-list'}`}>
        
        {/* PANEL DE CONTACTOS */}
        <div className="mi-contactos-panel">
            <div className="mi-contactos-header">
              <button
                type="button"
                className="mi-back-to-app-button"
                onClick={handleBackToAppHome}
              >
                ← Volver al panel
              </button>
              <h2>Contactos</h2>
              {isInstructorMessagingRole(usuarioInfo.rol) ? (
                <div className="mi-course-scope">
                  <label className="mi-course-scope-label" htmlFor="mi-course-scope-select">
                    {es.messaging.courseScopeLabel}
                  </label>
                  <ConstrainedSelect
                    id="mi-course-scope-select"
                    className="mi-course-scope-select"
                    value={cursoSeleccionado}
                    disabled={cargandoCursosAsignados}
                    onChange={handleCursoChange}
                    options={[
                      {
                        value: MESSAGING_STAFF_SCOPE_ADMIN,
                        label: es.messaging.courseScopeStaff,
                      },
                      ...cursosAsignados.map((curso) => {
                        const cid = normalizeMongoId(curso._id);
                        return {
                          value: cid,
                          label: curso.titulo || cid,
                        };
                      }),
                    ]}
                  />
                  {!cargandoCursosAsignados && cursosAsignados.length === 0 ? (
                    <p className="mi-course-scope-hint">{es.messaging.courseScopeEmpty}</p>
                  ) : null}
                </div>
              ) : null}
              {usuarioInfo.rol === "director" ? (
                <div className="mi-course-scope">
                  <label className="mi-course-scope-label" htmlFor="mi-director-scope-select">
                    Tipo de destinatario
                    {hasUnreadOutsideDirectorScope ? (
                      <span
                        className="mi-course-scope-unread-dot"
                        role="status"
                        aria-label="Hay mensajes nuevos en otro tipo de destinatario"
                        title="Hay mensajes nuevos en otro tipo de destinatario"
                      />
                    ) : null}
                  </label>
                  <ViewportSelect
                    id="mi-director-scope-select"
                    className={`mi-course-scope-select${hasUnreadOutsideDirectorScope ? " has-unread" : ""}`}
                    value={directorScope}
                    disabled={cargandoCursosAsignados}
                    onChange={(e) => handleDirectorScopeChange(e.target.value)}
                  >
                    <option value={MESSAGING_DIRECTOR_SCOPE_USERS}>
                      {directorScopeUnreadCounts[MESSAGING_DIRECTOR_SCOPE_USERS] > 0
                        ? `Usuarios (${directorScopeUnreadCounts[MESSAGING_DIRECTOR_SCOPE_USERS]} nuevo${directorScopeUnreadCounts[MESSAGING_DIRECTOR_SCOPE_USERS] === 1 ? "" : "s"})`
                        : "Usuarios"}
                    </option>
                    <option value={MESSAGING_DIRECTOR_SCOPE_ADMINS}>
                      {directorScopeUnreadCounts[MESSAGING_DIRECTOR_SCOPE_ADMINS] > 0
                        ? `Administradores (${directorScopeUnreadCounts[MESSAGING_DIRECTOR_SCOPE_ADMINS]} nuevo${directorScopeUnreadCounts[MESSAGING_DIRECTOR_SCOPE_ADMINS] === 1 ? "" : "s"})`
                        : "Administradores"}
                    </option>
                    <option value={MESSAGING_DIRECTOR_SCOPE_COURSES}>
                      {directorScopeUnreadCounts[MESSAGING_DIRECTOR_SCOPE_COURSES] > 0
                        ? `Cursos (${directorScopeUnreadCounts[MESSAGING_DIRECTOR_SCOPE_COURSES]} nuevo${directorScopeUnreadCounts[MESSAGING_DIRECTOR_SCOPE_COURSES] === 1 ? "" : "s"})`
                        : "Cursos"}
                    </option>
                  </ViewportSelect>
                  {directorScope === MESSAGING_DIRECTOR_SCOPE_COURSES ? (
                    <>
                      <label className="mi-course-scope-label mi-course-scope-label--nested" htmlFor="mi-director-course-select">
                        Curso
                      </label>
                      <ConstrainedSelect
                        id="mi-director-course-select"
                        className="mi-course-scope-select"
                        value={isMessagingCourseScope(cursoSeleccionado) ? cursoSeleccionado : ""}
                        disabled={cargandoCursosAsignados || cursosAsignados.length === 0}
                        onChange={handleCursoChange}
                        options={
                          cursosAsignados.length === 0
                            ? [{ value: "", label: es.messaging.courseSelectEmpty, disabled: true }]
                            : cursosAsignados.map((curso) => {
                              const cid = normalizeMongoId(curso._id);
                              return {
                                value: cid,
                                label: curso.titulo || cid,
                              };
                            })
                        }
                      />
                    </>
                  ) : null}
                </div>
              ) : null}
              <div className="mi-buscador">
                <input
                  type="text"
                  className="mi-search"
                  placeholder={es.messaging.filterActiveConversations}
                  value={busqueda}
                  onChange={(e) => setBusqueda(e.target.value)}
                  aria-label={es.messaging.filterActiveConversations}
                />
              </div>
              {usuarioInfo.rol !== "alumno" && usuarioInfo.rol !== "director" ? (
                <div className="mi-role-filters" role="group" aria-label="Filtrar contactos por rol">
                  {[
                    { id: "all", label: "Todos" },
                    { id: "alumno", label: "Alumnos" },
                    { id: "instructor", label: "Instructores" },
                    { id: "director", label: "Administración" },
                  ].map((chip) => (
                    <button
                      key={chip.id}
                      type="button"
                      className={`mi-role-filter-btn ${contactRoleFilter === chip.id ? "is-active" : ""}`}
                      onClick={() => setContactRoleFilter(chip.id)}
                    >
                      {chip.label}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>

            <div className="mi-content-lista">
              <div className="mi-user-list-container">
                {contactosLoading ||
                (String(contactosFiltroActual || "").trim() !== "" &&
                  String(contactosFiltroActual) !== String(cursoSeleccionado || "")) ? (
                  <p className="mi-contacts-loading" role="status">Actualizando conversaciones…</p>
                ) : null}
                <ul className="mi-user-list">
                  {contactosParaLista.map((contacto) => {
                      const isActive = isSameConversation(conversacionActiva, contacto);
                      const displayCount = Number(contacto.unreadCount) || 0;
                      const threadIdentity = identityFromContact(contacto);
                      const visibleContactName =
                        usuarioInfo.rol === "alumno" && contacto.nombreVisibleConversacion
                          ? contacto.nombreVisibleConversacion
                          : contacto.nombre;
                      const visibleContactRole =
                        usuarioInfo.rol === "alumno"
                          ? contacto.rolVisibleConversacion || threadIdentity.rol
                          : formatConversationIdentityLabel(es.messaging.contactIdentityLabel, {
                            name: threadIdentity.nombre,
                            role: threadIdentity.rol,
                          });
                      const contactListLabel =
                        visibleContactName && visibleContactRole
                          ? formatConversationIdentityLabel(es.messaging.contactIdentityLabel, {
                            name: visibleContactName,
                            role: visibleContactRole,
                          })
                          : usuarioInfo.rol === "alumno" && visibleContactName
                            ? visibleContactName
                          : formatMessagingContactLabel(contacto);
                      const unreadLabel =
                        displayCount === 1 ? "1 mensaje sin leer" : `${displayCount} mensajes sin leer`;
                      const isDeepLinkedUnread =
                        displayCount > 0 &&
                        highlightedUnreadContactId === contactoId(contacto) &&
                        (
                          !new URLSearchParams(location.search).get("identidad") ||
                          threadIdentity.key === new URLSearchParams(location.search).get("identidad")
                        );

                      return (
                        <li
                          key={conversationRowKey(contacto) || contactoId(contacto) || "contacto"}
                          className={`mi-user-item ${isActive ? 'selected' : ''}${displayCount > 0 ? ' unread' : ''}${isDeepLinkedUnread ? ' unread-target' : ''}`}
                          onClick={() => handleSeleccionarContacto(contacto)}
                          aria-label={`${contactListLabel}${displayCount > 0 ? `, ${unreadLabel}` : ""}`}
                        >
                          <div className="mi-contact-wrapper">
                            <div className="mi-avatar-container">
                                <div className="mi-avatar">
                                    {visibleContactName ? visibleContactName.charAt(0).toUpperCase() : "?"}
                                </div>
                                <span
                                  className={`mi-presence-dot ${contacto.enLinea ? "is-online" : "is-offline"}`}
                                  title={contacto.enLinea ? "Conectado" : "Desconectado"}
                                  aria-hidden="true"
                                />
                                <NotificationBadge count={displayCount} />
                            </div>
                            <div className="mi-contact-info">
                                <div className="mi-contact-name">{contactListLabel}</div>
                                <div className="mi-contact-role">
                                    {contacto.enLinea ? "En línea" : "Desconectado"}
                                </div>
                                {displayCount > 0 ? (
                                  <div className="mi-contact-unread-hint">
                                    {unreadLabel}
                                  </div>
                                ) : null}
                            </div>
                            
                            {displayCount > 0 && (
                                <div 
                                    className="new-message-indicator"
                                    aria-label="Mensajes nuevos"
                                    title="Nuevos mensajes"
                                />
                            )}
                          </div>
                        </li>
                      );
                    })}
                </ul>

                {usuarioInfo.rol !== "alumno" ? (
                  <>
                    <h3 className="mi-start-chat-heading">{es.messaging.startChatHeading}</h3>
                    <div className="mi-buscador mi-buscador--start-chat">
                      <input
                        type="text"
                        className="mi-search"
                        placeholder={es.messaging.startChatPlaceholder}
                        value={busquedaInicioChat}
                        onChange={(e) => setBusquedaInicioChat(e.target.value)}
                        aria-label={es.messaging.startChatPlaceholder}
                      />
                    </div>
                    {busquedaInicioChat.trim().length > 0 && busquedaInicioChat.trim().length < 2 && (
                      <p className="mi-start-chat-hint">{es.messaging.startChatMinHint}</p>
                    )}
                    {errorBusquedaInicio ? (
                      <p className="mi-start-chat-error">{errorBusquedaInicio}</p>
                    ) : null}
                    {cargandoBusquedaInicio ? (
                      <p className="mi-start-chat-loading">{es.messaging.startChatSearching}</p>
                    ) : null}
                    {!cargandoBusquedaInicio &&
                    !errorBusquedaInicio &&
                    busquedaInicioChat.trim().length >= 2 &&
                    resultadosInicioFiltrados.length === 0 ? (
                      <p className="mi-start-chat-empty">{es.messaging.startChatEmpty}</p>
                    ) : (
                      <ul className="mi-user-list mi-user-list--start-chat">
                        {resultadosInicioFiltrados.map((contacto) => (
                          <li
                            key={`start-${contactoId(contacto) || "x"}`}
                            className="mi-user-item"
                            onClick={() => {
                              const startIdentity = buildConversationIdentity({
                                nombre: visibleSenderName,
                                rol: visibleSenderRole,
                              });
                              handleSeleccionarContacto({
                                ...contacto,
                                identidadConversacion: startIdentity,
                                identidadKey: startIdentity.key,
                              });
                            }}
                          >
                            <div className="mi-contact-wrapper">
                              <div className="mi-avatar-container">
                                <div className="mi-avatar">
                                  {contacto.nombre ? contacto.nombre.charAt(0).toUpperCase() : "?"}
                                </div>
                              </div>
                              <div className="mi-contact-info">
                                <div className="mi-contact-name">
                                  {formatMessagingContactLabel(contacto)}
                                </div>
                                <div className="mi-contact-role">
                                  {contacto.email || formatRol(contactRoleLabelForDisplay(contacto)) || "—"}
                                </div>
                              </div>
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </>
                ) : contactosParaLista.length === 0 ? (
                  <p className="mi-start-chat-empty">{es.messaging.studentNoConversations}</p>
                ) : null}
              </div>
            </div>
        </div>

        {/* AREA DE CHAT */}
        <div className="mi-content-chat">
            {conversacionActiva ? (
              <>
                <div className="mi-chat-header">
                  <button className="mi-back-button" onClick={handleBackToContacts} aria-label="Volver a contactos">
                    ←
                  </button>
                  <div className="mi-chat-header-info">
                    <h2>
                      {activeConversationName}
                    </h2>
                    <span className="mi-chat-subtitle">
                      {activeConversationRole}
                      {usuarioInfo.rol !== "alumno" && conversacionActiva.email
                        ? ` · ${conversacionActiva.email}`
                        : ""}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="mi-back-to-app-button mi-back-to-app-button--header"
                    onClick={handleBackToAppHome}
                    aria-label="Volver al panel de la aplicación"
                  >
                    Volver al panel
                  </button>
                </div>

                <div
                  className="mi-messages-area"
                  ref={chatContainerRef}
                  onScroll={(e) => {
                    const el = e.currentTarget;
                    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
                    setAutoScroll(nearBottom);
                    
                    if (el.scrollTop === 0 && !loadingMore && !allLoaded) {
                        loadMoreHistory();
                    }
                  }}
                >
                  {loadingMore && <div className="mi-loading-more">Cargando anteriores...</div>}
                  {renderMensajes()}
                </div>

                <div className="mi-escribir-contenedor">
                  {canConfigureVisibleSender ? (
                    <div className="mi-visible-sender-config" aria-label="Identidad visible para el alumno">
                      <label className="mi-visible-sender-field">
                        <span>Nombre visible para el alumno</span>
                        <input
                          type="text"
                          value={visibleSenderName}
                          maxLength={80}
                          onChange={(e) => setVisibleSenderName(e.target.value)}
                          placeholder="Ej. Secretaría académica"
                        />
                      </label>
                      <label className="mi-visible-sender-field">
                        <span>Rol visible</span>
                        <input
                          type="text"
                          value={visibleSenderRole}
                          maxLength={40}
                          onChange={(e) => setVisibleSenderRole(e.target.value)}
                          placeholder="Ej. Soporte, Tutor, Dirección"
                        />
                      </label>
                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm"
                        onClick={handleSaveVisibleSender}
                        disabled={visibleSenderSaving}
                      >
                        {visibleSenderSaving ? "Guardando..." : "Guardar identidad"}
                      </button>
                      {visibleSenderStatus ? (
                        <span role="status" className="mi-visible-sender-status">
                          {visibleSenderStatus}
                        </span>
                      ) : null}
                    </div>
                  ) : null}
                  <div className="mi-compose-row">
                    <textarea
                      className="recuadro-escribir"
                      value={mensaje}
                      onChange={(e) => setMensaje(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder={
                        !canChat && isCursoReal
                          ? "Debes integrarte al curso para escribir"
                          : "Escribe tu mensaje..."
                      }
                      disabled={!canChat || !conversacionActiva}
                    />
                    <button
                      onClick={handleEnviarMensaje}
                      className="boton-recuadro"
                      disabled={!canChat || !conversacionActiva}
                      aria-label="Enviar mensaje"
                    >
                      <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
                        <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"></path>
                      </svg>
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className="mi-no-chat-selected">
                <button
                  type="button"
                  className="mi-back-to-app-button mi-back-to-app-button--inline"
                  onClick={handleBackToAppHome}
                >
                  ← Volver al panel
                </button>
                <div className="mi-empty-state-icon">💬</div>
                <p>Seleccione un contacto para comenzar a chatear.</p>
              </div>
            )}
        </div>
      </div>
    </div>
  );
};

export default MensajeriaInterna;
